import type { Editor } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export type StoredSelectionRange = { from: number; to: number };

export const storedSelectionPluginKey = new PluginKey<StoredSelectionRange | null>(
    "storedSelectionHighlight"
);

/** Set or clear the stored selection highlight. Call this instead of a command to avoid type issues. */
export function setStoredSelection(editor: Editor, range: StoredSelectionRange | null): void {
    const tr = editor.state.tr.setMeta(storedSelectionPluginKey, range);
    tr.setMeta("addToHistory", false);
    editor.view.dispatch(tr);
}

/** Extension that stores a selection range and shows a highlight decoration when the editor loses focus (e.g. when using the color picker). */
export const StoredSelectionHighlight = Extension.create({
    name: "storedSelectionHighlight",

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: storedSelectionPluginKey,
                state: {
                    init: () => null as StoredSelectionRange | null,
                    apply: (tr, value) => {
                        const meta = tr.getMeta(storedSelectionPluginKey);
                        if (meta !== undefined) return meta;
                        return value;
                    },
                },
                props: {
                    decorations(state) {
                        const stored = storedSelectionPluginKey.getState(state);
                        if (!stored || stored.from >= stored.to) return null;
                        try {
                            return DecorationSet.create(state.doc, [
                                Decoration.inline(stored.from, stored.to, {
                                    class: "stored-selection-highlight",
                                }),
                            ]);
                        } catch {
                            return null;
                        }
                    },
                },
            }),
        ];
    },
});
